/**
 * Error handling utilities for consistent error management across the application
 */

/**
 * Extract error message from various error response formats
 * @param {Object} error - The error object (could be axios error, response, or custom error)
 * @returns {string} - Formatted error message
 */
export const extractErrorMessage = (error) => {
  if (!error) return "Errore sconosciuto";
  
  // Handle axios error structure
  const response = error.response || error;
  const data = response?.data || response;
  
  if (typeof data === 'string') return data;
  
  // Try multiple possible error message fields
  const message = data?.message || 
                 data?.Message || 
                 data?.error || 
                 data?.Error || 
                 data?.errorMessage || 
                 data?.detail || 
                 data?.title ||
                 data?.errors;
  
  // Handle validation errors (array format)
  if (Array.isArray(message)) {
    return message.join('<br>');
  }
  
  // Handle object with multiple error fields
  if (typeof message === 'object' && message !== null) {
    const errorMessages = [];
    Object.keys(message).forEach(key => {
      const value = message[key];
      if (Array.isArray(value)) {
        errorMessages.push(`${key}: ${value.join(', ')}`);
      } else {
        errorMessages.push(`${key}: ${value}`);
      }
    });
    return errorMessages.length > 0 ? errorMessages.join('<br>') : 'Errore di validazione';
  }
  
  if (message) return message;
  
  // Fallback based on HTTP status
  const status = response?.status;
  switch (status) {
    case 400: return 'Richiesta non valida';
    case 401: return 'Non autorizzato';
    case 403: return 'Accesso negato';
    case 404: return 'Risorsa non trovata';
    case 422: return 'Dati non validi';
    case 500: return 'Errore interno del server';
    case 502: return 'Servizio non disponibile (Bad Gateway)';
    case 503: return 'Servizio temporaneamente non disponibile';
    default: return status ? `Errore HTTP ${status}` : 'Errore sconosciuto';
  }
};

/**
 * Handle error in Vuex actions with consistent error extraction and notification
 * @param {Object} context - Vuex action context
 * @param {Object} error - The error object
 * @param {string} defaultMessage - Default message if no specific error found
 * @param {string} loadingMutation - Optional mutation to set loading to false
 */
export const handleVuexError = (context, error, defaultMessage = null, loadingMutation = null) => {
  // Stop loading if mutation provided
  if (loadingMutation) {
    context.commit(loadingMutation, false);
  }
  
  const errorMessage = extractErrorMessage(error) || defaultMessage || "Si è verificato un errore";
  
  // Commit to local error state if available
  if (Object.prototype.hasOwnProperty.call(context.state, 'error')) {
    context.commit('setError', errorMessage);
    setTimeout(() => context.commit('setError', ''), 5000);
  }
  
  // Also dispatch to global notifications
  context.dispatch('notifications/showError', errorMessage, { root: true });
};

/**
 * Handle success in Vuex actions with consistent success notification
 * @param {Object} context - Vuex action context
 * @param {string} message - Success message
 * @param {string} loadingMutation - Optional mutation to set loading to false
 */
export const handleVuexSuccess = (context, message, loadingMutation = null) => {
  // Stop loading if mutation provided
  if (loadingMutation) {
    context.commit(loadingMutation, false);
  }
  
  // Commit to local success state if available
  if (Object.prototype.hasOwnProperty.call(context.state, 'success')) {
    context.commit('setSuccess', message);
    setTimeout(() => context.commit('setSuccess', ''), 5000);
  }
  
  // Also dispatch to global notifications
  context.dispatch('notifications/showSuccess', message, { root: true });
};

/**
 * Create a standardized error handler for API calls
 * @param {Object} context - Vuex action context
 * @param {string} defaultMessage - Default error message
 * @param {string} loadingMutation - Optional mutation to set loading to false
 * @returns {Function} - Error handler function
 */
export const createErrorHandler = (context, defaultMessage, loadingMutation = null) => {
  return (error) => {
    handleVuexError(context, error, defaultMessage, loadingMutation);
    throw error; // Re-throw for component handling if needed
  };
};

/**
 * Wrapper for API calls with standard error handling
 * @param {Object} context - Vuex action context  
 * @param {Promise} apiCall - The API call promise
 * @param {Object} options - Configuration options
 * @returns {Promise} - The API call result
 */
export const withErrorHandling = async (context, apiCall, options = {}) => {
  const {
    defaultError = "Si è verificato un errore",
    loadingMutation = null,
    successMessage = null
  } = options;
  
  try {
    const result = await apiCall;
    
    if (successMessage) {
      handleVuexSuccess(context, successMessage, loadingMutation);
    } else if (loadingMutation) {
      context.commit(loadingMutation, false);
    }
    
    return result;
  } catch (error) {
    handleVuexError(context, error, defaultError, loadingMutation);
    throw error;
  }
};